import * as React from 'react';
import { useState } from 'react';
import { 
	Table,
	Toolbar,
	ToolbarItem,
	TextPassage, 
	Button, 
	Icon,
	Section,
	Alert,
	AlertVariantType
} from '../common';
import { ContainerLayout } from '@emorg-prd/standard-react';
import BulkUploadModal from './components/BulkUploadModal';
import Paginator from '../common/tables/Paginator';
import { AlertBanner } from '../../models/interfaces/AlertBanner';
import AADAppsImportProps from './AADAppsImportTableRow';
import '../common/wrappers/SectionHomeWrapper.css';
import { LinkTree, LinkTreeItem } from '../common/LinkTree';

interface AADAppsImportProps
{ 
	apps: [];
	onChange: (apps: []) => any;
}

const AADAppsImport = ({linkTreeText, linkTreeRedirectTo, title, applicationsService}, {apps, onChange} : AADAppsImportProps) => {

	const [filteredApplications, setFilteredApplications] = useState<[]>([]);
	const [appsOnPage, setAppsOnPage] = useState<[]>([]);
	const [submissionAlert, setSubmissionAlert] = useState<AlertBanner>({
														visible: false,
														text: ''
													});
	const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState<boolean>(false);
	const [uploadingApps, setUploadingApps] = useState<boolean>(false);

	const handleBulkUpload = (applications: []) => {
		setBulkUploadModalVisible(false);
		setFilteredApplications(applications);
		onChange([...apps, ...applications]);
	};

	const importApplications = async () => {
		try {
			setSubmissionAlert({
					visible: true,
					text: 'Importing Applications'
			});
			setUploadingApps(true);
			
			await applicationsService.import(filteredApplications);
			setSubmissionAlert({
				visible: true,
				text: 'Applications queued for upload. You will receive a notification via email once the process has finished.',
				variant: 'success'
			});
			setUploadingApps(false);
		} catch(e) {
			setSubmissionAlert({
				visible: true,
				text: e.response?.data.message || 'An error occurred while importing the applications. Error message: ' + e,
				variant: 'error'
			});
		}
	};

	const showBulkModal = async () => {
		setBulkUploadModalVisible(true);
	};

	const getLinkTree = (): LinkTreeItem[] => {
		return [
			{ text: `${linkTreeText}`, to: `${linkTreeRedirectTo}` },
		];
	};


	return (
		<>
			<ContainerLayout optionalClass="em-u-max-width-layout em-u-margin-top-double em-u-margin-bottom-double">
				<LinkTree links={getLinkTree()} />
				<TextPassage>
					<h2 className="em-u-margin-top-double">
						{
							title
						}
					</h2>
				</TextPassage>
				<TextPassage>
						<p>
					Here you can process the data input from the received csv application file. You can select the file that contains the information to insert, 
					the file format is validated, it is processed following a defined logic and finally the data is stored in the platform&apos;s database.
						</p>
				</TextPassage>
				{submissionAlert.visible && (
					<Alert
						variant={submissionAlert.variant as AlertVariantType}
						body={submissionAlert.text}
					/>
				)}
				<Section title="File Data (preview):">
				<Table
						header={
							<Toolbar>
								<ToolbarItem right>
									<Button
										variant="primary"
										disabled={false}
										onClick={showBulkModal}
									>
										<Icon icon="check-out-doc-2" size="small" />
										<span>Bulk Upload</span>
									</Button>
								</ToolbarItem>
							</Toolbar>
						}
						footer={
							<Paginator
								data={filteredApplications}
								onPageChange={(apps: any) => {
									setAppsOnPage(apps);
								}}
							/>
						}
					>
						<Table.Head>
							<Table.Head.Row>
								<Table.Head.Cell>Application Id</Table.Head.Cell>
								<Table.Head.Cell>Owner UPN</Table.Head.Cell>
								<Table.Head.Cell>Environment</Table.Head.Cell>
								<Table.Head.Cell>Expiration Date</Table.Head.Cell>
								<Table.Head.Cell>DBM Id</Table.Head.Cell>
							</Table.Head.Row>
						</Table.Head>
						<Table.Body>
							{appsOnPage.map((app) => (
								<AADAppsImportProps
									application={app}
								/>
							))}
						</Table.Body>
					</Table>
			</Section>
				{bulkUploadModalVisible && <BulkUploadModal
					applicationService={applicationsService}
					visible={bulkUploadModalVisible}
					onSuccess={handleBulkUpload}
					onCancel={(ev) => {
						setBulkUploadModalVisible(false);
					}}
				/>}
			<Button
				variant="primary"
				loading={uploadingApps}
				disabled={!filteredApplications?.length || uploadingApps}
				onClick={importApplications}
			>
				<span>Upload</span>
			</Button>
			</ContainerLayout>
		</>
	);
}

export default AADAppsImport;